import { createSelector } from '@reduxjs/toolkit';
import { constants } from 'config/constants';
import { sortLockersNaturalWay } from 'library/helpers/lockerHelpers';
import { selectCategoryFieldsByCategoryID } from 'reduxStore/categories/selectors';

//📌 Selector for get all devices from current profile
export const selectDevices = createSelector(
	[(state) => state.Devices.data, (state) => state.Auth.selectedProfile.orgID],
	(devicesData, orgID) => devicesData[orgID] || []
);

//📌 Selector for get device by deviceID.
export const selectDevicesByDeviceID = createSelector(
	[(state) => state.Devices.data, (state) => state.Auth.selectedProfile.orgID, (_, deviceID) => deviceID],
	(devicesData, orgID, deviceID) => (devicesData[orgID] || []).find((device) => device.deviceID === deviceID) || null
);

//📌 Selector for get all fields by deviceID.
export const selectDeviceFieldsByDeviceID = createSelector(
	[(state) => state.Devices.data, (state) => state.Auth.selectedProfile.orgID, (_, deviceID) => deviceID],
	(devicesData, orgID, deviceID) => devicesData[orgID]?.find((device) => device.deviceID === deviceID)?.fields || []
);

//📌 Selector for get all available fields to add to device by deviceID and categoryID.
export const selectAvailableCategoryFieldsToAdd = (deviceID, categoryID) =>
	createSelector(
		(state) => selectDeviceFieldsByDeviceID(state, deviceID),
		(state) => selectCategoryFieldsByCategoryID(state, categoryID),
		(deviceFields, categoryFields) => {
			if (!deviceFields || !categoryFields) return [];
			return categoryFields.filter(
				(categoryField) => !deviceFields.some((df) => df.fieldID === categoryField.fieldID)
			);
		}
	);

//📌 Selector for get all category fields (for modal editing)
export const selectAllCategoryFields = (categoryID) =>
	createSelector(
		(state) => selectCategoryFieldsByCategoryID(state, categoryID),
		(categoryFields) => categoryFields || []
	);

//📌 Selector for get all devices by categoryID
export const selectDevicesByCategoryID = createSelector(
	[(state) => state.Devices.data, (state) => state.Auth.selectedProfile.orgID, (_, categoryID) => categoryID],
	(devicesData, orgID, categoryID) =>
		(devicesData[orgID] || []).filter((device) => device.categoryID === categoryID) || []
);

//📌 Selector for get all devices by name.
export const selectDevicesByName = createSelector(
	[(state) => state.Devices.data, (state) => state.Auth.selectedProfile.orgID, (_, name) => name],
	(devicesData, orgID, name) =>
		(devicesData[orgID] || []).filter((device) => device.name.toLowerCase().includes(name.toLowerCase())) || []
);

//📌 Selector for get all devices by defaultCategoryID.
export const selectLockersDevices = createSelector(
	[(state) => state.Devices.data, (state) => state.Auth.selectedProfile.orgID],
	(devicesData, orgID) => {
		const allDevices = devicesData[orgID] || [];

		const bookableLockers = allDevices.filter((device) =>
			constants.BOOKEABLE_LOCKERS.includes(device.defaultCategoryID)
		);

		// 🔀 Apply natural sorting
		return sortLockersNaturalWay(bookableLockers);
	}
);

//📌 Selector for get all permission manageable devices.
export const selectPermissionManageableDevices = createSelector(
	[(state) => state.Devices.data, (state) => state.Auth.selectedProfile.orgID],
	(devicesData, orgID) => {
		const allDevices = devicesData[orgID] || [];
		return allDevices.filter((device) =>
			constants.PERMISSION_MANAGEABLE_CATEGORIES.includes(device.defaultCategoryID)
		);
	}
);

//📌 Selector for get all Control integration devices (locks only).
export const selectControlDevices = createSelector(
	[(state) => state.Devices.data, (state) => state.Auth.selectedProfile.orgID],
	(devicesData, orgID) => {
		const allDevices = devicesData[orgID] || [];
		return allDevices.filter((device) => constants.CONTROL_DEVICE_CATEGORIES.includes(device.defaultCategoryID));
	}
);
